import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Users, ClipboardCheck, AlertCircle, FileText, ArrowLeft, Shield, ClipboardList, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import TabNavigation from '../../components/TabNavigation';
import { usePermissions } from '../../context/PermissionContext';
import { canAccessSupervisorDashboard } from '@/dashboard/presets/safety';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// The Supervisor ("My") dashboard is a personal view: frontline workers see
// their own assigned items and the management roles that oversee them see theirs.
// (The endpoint only ever returns the caller's own data — this is a UX gate.)
// Access uses the SAME predicate as the Safety Hub tile and the App.js route
// guard (canAccessSupervisorDashboard in presets/safety.js), so a tile never
// leads to a page that redirects home. Previously a local role list here
// omitted corporate_safety_head and other analytics:read roles (QA 25/9/2026).

const SupervisorDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { isAdmin, getResourcePermissions, loading: permsLoading } = usePermissions();
  const allowed = permsLoading
    || canAccessSupervisorDashboard({ isAdmin, getResourcePermissions }, user?.role);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Restrict access to supervisor/management roles (UX/intent gate — the
  // endpoint itself only returns the caller's own data).
  useEffect(() => {
    if (!allowed) navigate('/');
  }, [allowed, navigate]);

  // Welcome banner — same once-per-session behaviour as the main Dashboard
  // (see pages/Dashboard.js). Independent sessionStorage key so the supervisor
  // and main-dashboard greetings can each show once on their own page.
  const [showWelcome, setShowWelcome] = useState(() => {
    try {
      return sessionStorage.getItem('supervisor_welcome_seen') !== '1';
    } catch (_e) {
      return true;
    }
  });

  useEffect(() => {
    if (allowed) fetchDashboard();
  }, [allowed]);

  useEffect(() => {
    if (!showWelcome) return;
    const timer = setTimeout(() => {
      setShowWelcome(false);
      try { sessionStorage.setItem('supervisor_welcome_seen', '1'); } catch (_e) { /* ignore */ }
    }, 6000);
    return () => clearTimeout(timer);
  }, [showWelcome]);

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/supervisor`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load dashboard');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="supervisor-dashboard">
      <TabNavigation user={user} onLogout={onLogout} />
      <div className="p-4">
      {/* Header with Back Button */}
      <Button
        variant="outline"
        onClick={() => navigate('/safety')}
        className="mb-4"
        data-testid="back-button"
        size="sm"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Safety Hub
      </Button>
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900">My Dashboard</h1>
        <p
          className={`text-gray-600 text-sm transition-all duration-700 ease-out overflow-hidden ${
            showWelcome ? 'opacity-100 max-h-8' : 'opacity-0 max-h-0 mt-0'
          }`}
          aria-hidden={!showWelcome}
        >
          Welcome, {dashboard.supervisor_name}
        </p>
      </div>

      {/* AI Summary Card */}
      <Card className="mb-4 border-blue-200 bg-blue-50">
        <CardContent className="pt-4">
          <p className="text-blue-900 text-sm leading-relaxed">{dashboard.ai_summary}</p>
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 mb-1">All Users</p>
                <p className="text-2xl font-bold text-blue-600">{dashboard.team_size}</p>
              </div>
              <Users className="w-8 h-8 text-blue-600 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 mb-1">Open CAPAs</p>
                <p className="text-2xl font-bold text-orange-600">{dashboard.my_open_capas}</p>
              </div>
              <ClipboardCheck className="w-8 h-8 text-orange-600 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 mb-1">Overdue CAPAs</p>
                <p className="text-2xl font-bold text-red-600">{dashboard.my_overdue_capas}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-red-600 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-600 mb-1">Observations</p>
                <p className="text-2xl font-bold text-green-600">{dashboard.my_observations_this_week}</p>
              </div>
              <FileText className="w-8 h-8 text-green-600 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* PTW Status */}
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">PTW Status</h2>
        <div className="grid grid-cols-3 gap-3">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/ptw')}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600 mb-1">Active PTWs</p>
                  <p className="text-2xl font-bold text-blue-600">{dashboard.my_active_ptws || 0}</p>
                </div>
                <Shield className="w-8 h-8 text-blue-600 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/ptw')}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600 mb-1">Pending Approvals</p>
                  <p className="text-2xl font-bold text-orange-600">{dashboard.my_pending_ptw_approvals || 0}</p>
                </div>
                <ClipboardList className="w-8 h-8 text-orange-600 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/ptw')}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600 mb-1">Extended PTWs</p>
                  <p className="text-2xl font-bold text-orange-600">{dashboard.my_ptw_extended || 0}</p>
                </div>
                <RefreshCw className="w-8 h-8 text-orange-600 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* All Users Performance */}
      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="text-lg">All Users Performance</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">All Users Competence Score</span>
            <div className="flex items-center">
              <div className="w-24 h-2 bg-gray-200 rounded-full mr-2">
                <div 
                  className="h-2 bg-green-500 rounded-full" 
                  style={{ width: `${dashboard.team_competence_score}%` }}
                />
              </div>
              <span className="text-sm font-semibold">{dashboard.team_competence_score}%</span>
            </div>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Certifications Expiring</span>
            <span className="font-semibold text-orange-600">{dashboard.team_certifications_expiring}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Hazards Reported</span>
            <span className="font-semibold">{dashboard.my_hazards_reported}</span>
          </div>
        </CardContent>
      </Card>

      {/* Priority Tasks */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Today's Priority Tasks</CardTitle>
        </CardHeader>
        <CardContent>
          {dashboard.priority_tasks.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-4">No priority tasks for today</p>
          ) : (
            <div className="space-y-2">
              {dashboard.priority_tasks.map((task, idx) => (
                <div key={idx} className="border-l-4 border-orange-500 pl-3 py-2 bg-gray-50 rounded">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-sm">{task.title}</span>
                    <span className={`text-xs px-2 py-1 rounded ${
                      task.severity === 'Critical' ? 'bg-red-100 text-red-800' :
                      task.severity === 'High' ? 'bg-orange-100 text-orange-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {task.severity}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600">Due: {task.due_date}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-3 mt-4">
        <Button 
          className="w-full" 
          variant="outline" 
          onClick={() => navigate('/report')}
          data-testid="report-hazard-btn"
        >
          Report Incident
        </Button>
        <Button 
          className="w-full" 
          variant="outline"
          onClick={() => navigate('/capa')}
          data-testid="view-capas-btn"
        >
          View My CAPAs
        </Button>
      </div>
      </div>
    </div>
  );
};

export default SupervisorDashboard;
